<template>
  <HrPageLayout
    title="HR Dashboard"
    subtitle="Performance – HR Review"
    icon="📋"
    :breadcrumb-items="[
      { label: 'Performance' },
      { label: 'HR Review' }
    ]"
  >
    <template #header-actions>
      <button @click="exportToExcel" class="hr-btn hr-btn-success hr-btn-sm">
        <i class="bx bx-file hr-me-1"></i> Export to Excel
      </button>
    </template>

    <div class="hr-dashboard-content">
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-file"></i></div>
          <p class="hr-metric-label">Total Appraisals</p>
          <h2 class="hr-metric-value">{{ statistics.total }}</h2>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <p class="hr-metric-label">Pending Review</p>
          <h2 class="hr-metric-value">{{ statistics.pendingReview }}</h2>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <p class="hr-metric-label">Approved</p>
          <h2 class="hr-metric-value">{{ statistics.approved }}</h2>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-star"></i></div>
          <p class="hr-metric-label">Avg Final Rating</p>
          <h2 class="hr-metric-value">{{ statistics.avgRating }}</h2>
        </div>
      </div>

      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body">
          <div class="hr-form-grid">
            <div class="hr-filter-group">
              <label class="hr-form-label">Department</label>
              <select v-model="filters.department" @change="loadAppraisals" class="hr-select">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept" :value="dept">{{ dept }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Status</label>
              <select v-model="filters.status" @change="loadAppraisals" class="hr-select">
                <option value="">All Statuses</option>
                <option value="Appraiser Review Complete">Pending My Review</option>
                <option value="Reviewer Complete">Reviewed</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Year</label>
              <select v-model="filters.year" @change="loadAppraisals" class="hr-select">
                <option value="">All Years</option>
                <option value="2025">2025</option>
                <option value="2024">2024</option>
                <option value="2023">2023</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Rating</label>
              <select v-model="filters.rating" @change="loadAppraisals" class="hr-select">
                <option value="">All Ratings</option>
                <option value="5">5 - Outstanding</option>
                <option value="4">4 - Good</option>
                <option value="3">3 - Average</option>
                <option value="2">2 - Below Avg</option>
                <option value="1">1 - Poor</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Search</label>
              <input type="text" v-model="filters.search" @input="searchAppraisals" class="hr-input" placeholder="Employee or manager...">
            </div>
            <div class="hr-filter-group hr-flex hr-align-end">
              <button @click="clearFilters" class="hr-btn hr-btn-secondary"><i class="bx bx-refresh"></i></button>
            </div>
          </div>
        </div>
      </div>

      <div class="hr-chart-card">
        <div class="hr-chart-header hr-flex-between">
          <h5 class="hr-chart-title">All Appraisals</h5>
        </div>
        <div class="hr-chart-body">
          <div v-if="loading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Loading appraisals...</p>
          </div>
          <div v-else-if="filteredAppraisals.length > 0" class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Department</th>
                  <th>Position</th>
                  <th>Period</th>
                  <th>Manager</th>
                  <th>Status</th>
                  <th>Self</th>
                  <th>Manager</th>
                  <th>Final</th>
                  <th class="hr-text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="appraisal in paginatedAppraisals" :key="appraisal.id">
                  <td>
                    <div class="hr-flex hr-align-center hr-gap-2">
                      <span class="hr-avatar-initials">{{ getInitials(appraisal.employeeName) }}</span>
                      <div>
                        <strong>{{ appraisal.employeeName }}</strong>
                        <div class="hr-text-muted hr-small">{{ appraisal.appraisalYear }}</div>
                      </div>
                    </div>
                  </td>
                  <td>{{ appraisal.department }}</td>
                  <td>{{ appraisal.positionTitle }}</td>
                  <td><small>{{ appraisal.periodStart }}</small><br><small>{{ appraisal.periodEnd }}</small></td>
                  <td><small>{{ appraisal.appraiserName || '-' }}</small></td>
                  <td><span :class="getStatusBadgeClass(appraisal.status)" class="hr-badge">{{ appraisal.status }}</span></td>
                  <td>
                    <span v-if="appraisal.sectionA_SelfOverallRating" class="hr-badge hr-badge-primary">{{ parseFloat(appraisal.sectionA_SelfOverallRating).toFixed(1) }}</span>
                    <span v-else class="hr-text-muted">-</span>
                  </td>
                  <td>
                    <span v-if="appraisal.sectionA_AppraiserOverallRating" class="hr-badge hr-badge-info">{{ parseFloat(appraisal.sectionA_AppraiserOverallRating).toFixed(1) }}</span>
                    <span v-else class="hr-text-muted">-</span>
                  </td>
                  <td>
                    <span v-if="appraisal.finalOverallRating" class="hr-badge hr-badge-success">{{ parseFloat(appraisal.finalOverallRating).toFixed(1) }}</span>
                    <span v-else class="hr-text-muted">-</span>
                  </td>
                  <td class="hr-text-center">
                    <div class="hr-flex hr-gap-1 hr-justify-center">
                      <button @click="viewAppraisal(appraisal.id)" class="hr-btn hr-btn-sm hr-btn-outline-info" title="View"><i class="bx bx-show"></i></button>
                      <button v-if="canReview(appraisal.status)" @click="reviewAppraisal(appraisal.id)" class="hr-btn hr-btn-sm hr-btn-outline-success" title="Final Review"><i class="bx bx-check-circle"></i></button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
            <div class="hr-pagination-wrap hr-flex-between hr-mt-3">
              <div class="hr-text-muted hr-small">Showing {{ ((currentPage - 1) * perPage) + 1 }} to {{ Math.min(currentPage * perPage, filteredAppraisals.length) }} of {{ filteredAppraisals.length }} entries</div>
              <nav class="hr-pagination">
                <button type="button" class="hr-page-btn" :disabled="currentPage === 1" @click="currentPage--">Previous</button>
                <button v-for="page in totalPages" :key="page" type="button" class="hr-page-btn" :class="{ active: currentPage === page }" @click="currentPage = page">{{ page }}</button>
                <button type="button" class="hr-page-btn" :disabled="currentPage === totalPages" @click="currentPage++">Next</button>
              </nav>
            </div>
          </div>
          <div v-else class="hr-empty-state">
            <div class="hr-empty-icon"><i class="bx bx-folder-open"></i></div>
            <h5>No Appraisals Found</h5>
            <p class="hr-text-muted">There are no appraisals matching your filters.</p>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";

export default {
  name: "HRDashboard",
  components: { HrPageLayout },

  data() {
    return {
      loading: false,
      appraisals: [],
      filteredAppraisals: [],
      departments: [],
      filters: {
        department: '',
        status: '',
        year: '',
        rating: '',
        search: ''
      },
      statistics: {
        total: 0,
        pendingReview: 0,
        approved: 0,
        avgRating: '0.00'
      },
      currentPage: 1,
      perPage: 10
    };
  },

  computed: {
    totalPages() {
      return Math.ceil(this.filteredAppraisals.length / this.perPage);
    },

    paginatedAppraisals() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.filteredAppraisals.slice(start, end);
    }
  },

  mounted() {
    this.loadDepartments();
    this.loadAppraisals();
  },

  methods: {
    async loadDepartments() {
      try {
        const data = new FormData();
        data.append("function", "getAllDepartments");

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.departments = res.data.data;
        }
      } catch (error) {
        console.error("Failed to load departments:", error);
      }
    },

    async loadAppraisals() {
      this.loading = true;

      try {
        const data = new FormData();
        data.append("function", "getAllAppraisalsForHR");
        data.append("department", this.filters.department);
        data.append("status", this.filters.status);
        data.append("year", this.filters.year);
        data.append("rating", this.filters.rating);

        const res = await execute(data, authUrl);

        if (res && res.data && res.data.success) {
          this.appraisals = res.data.data;
          this.applyFilters();
          this.calculateStatistics();
        } else {
          this.appraisals = [];
        }
      } catch (error) {
        console.error("Failed to load appraisals:", error);
        this.$toast?.error("Failed to load appraisals") || alert("Failed to load appraisals");
      } finally {
        this.loading = false;
      }
    },

    applyFilters() {
      let filtered = [...this.appraisals];

      // Search filter
      if (this.filters.search) {
        const search = this.filters.search.toLowerCase();
        filtered = filtered.filter(a =>
            a.employeeName.toLowerCase().includes(search) ||
            a.department.toLowerCase().includes(search) ||
            (a.appraiserName && a.appraiserName.toLowerCase().includes(search))
        );
      }

      this.filteredAppraisals = filtered;
      this.currentPage = 1; // Reset to first page
    },

    searchAppraisals() {
      this.applyFilters();
    },

    clearFilters() {
      this.filters = {
        department: '',
        status: '',
        year: '',
        rating: '',
        search: ''
      };
      this.loadAppraisals();
    },

    calculateStatistics() {
      this.statistics.total = this.appraisals.length;

      this.statistics.pendingReview = this.appraisals.filter(a =>
          a.status === 'Appraiser Review Complete' || a.status === 'Reviewer Pending'
      ).length;

      this.statistics.approved = this.appraisals.filter(a =>
          a.status === 'Approved'
      ).length;

      // Calculate average final rating
      const ratingsWithValues = this.appraisals.filter(a => a.finalOverallRating);
      if (ratingsWithValues.length > 0) {
        const sum = ratingsWithValues.reduce((acc, a) => acc + parseFloat(a.finalOverallRating), 0);
        this.statistics.avgRating = (sum / ratingsWithValues.length).toFixed(2);
      } else {
        this.statistics.avgRating = '0.00';
      }
    },

    viewAppraisal(id) {
      this.$router.push({
        path: '/hr-appraisal-view',
        query: { id: id }
      });
    },

    reviewAppraisal(id) {
      this.$router.push({
        path: '/hr-review-form',
        query: { id: id }
      });
    },

    canReview(status) {
      return status === 'Appraiser Review Complete' || status === 'Reviewer Pending';
    },

    exportToExcel() {
      // Implement Excel export
      this.$toast?.info("Excel export coming soon!") || alert("Excel export coming soon!");
    },

    getStatusBadgeClass(status) {
      const classes = {
        'Draft': 'hr-badge-secondary',
        'Self-Assessment Complete': 'hr-badge-info',
        'Appraiser Review Pending': 'hr-badge-warning',
        'Appraiser Review Complete': 'hr-badge-warning',
        'Reviewer Pending': 'hr-badge-warning',
        'Reviewer Complete': 'hr-badge-primary',
        'HR/CEO Review Pending': 'hr-badge-primary',
        'Approved': 'hr-badge-success',
        'Rejected': 'hr-badge-danger'
      };
      return classes[status] || 'hr-badge-secondary';
    },

    getInitials(name) {
      if (!name) return '?';
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name.substring(0, 2).toUpperCase();
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hr-dashboard-content .hr-metric-hint { font-size: 0.875rem; color: var(--hr-muted, #6c757d); }
.hr-fw-bold { font-weight: 600; }
.hr-avatar-initials { height: 40px; width: 40px; min-width: 40px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-weight: 600; font-size: 0.875rem; }
.hr-table-wrapper td { white-space: nowrap; }
</style>